import { observable, action, computed } from 'mobx';
import PAYMENT_PROVIDERS, { PAYMENT_MODES } from '../../constants/PaymentProviders';
import { IPaymentStrategy } from '../../payment-strategies/PaymentStrategyBase';
import { getPaymentDescriptorId } from '../../payment-strategies/PaymentDescriptor';
import Status from '../../constants/Status';

export interface IPaymentStrategiesMap {
  [paymentDescriptorId: string]: IPaymentStrategy;
}

export default class PaymentService {
  @observable paymentStrategies: IPaymentStrategiesMap = {};

  @observable paymentProvider: PAYMENT_PROVIDERS;

  @observable paymentMode: PAYMENT_MODES;

  @computed get paymentStatus(): Status {
    const paymentDescriptorId = getPaymentDescriptorId(
      this.paymentProvider,
      this.paymentMode,
    );
    const status: Status = this.paymentStrategies[paymentDescriptorId]?.status;
    return status;
  }

  @computed get paymentStrategy(): IPaymentStrategy {
    const paymentDescriptorId = getPaymentDescriptorId(
      this.paymentProvider,
      this.paymentMode,
    );
    return this.paymentStrategies[paymentDescriptorId];
  }

  @computed get isPayingInProgress(): boolean {
    return Object.values(this.paymentStrategies).some( item => item.status === Status.PAYING );
  }

  @action
  setPayment(paymentProvider: PAYMENT_PROVIDERS, paymentMode?: PAYMENT_MODES): void {
    this.paymentProvider = paymentProvider;
    this.paymentMode = paymentMode;

    // inconsistency workaround - default to CC if no method specific for adyen
    // need backend to add CC payment_mode in all brands - reference ticket ID: RCO-4840
    if (paymentProvider === PAYMENT_PROVIDERS.ADYEN_WEB && !paymentMode) {
      this.paymentMode = PAYMENT_MODES.CREDIT_CARD;
    }
    if (paymentProvider === PAYMENT_PROVIDERS.INGENICO && !paymentMode) {
      this.paymentMode = PAYMENT_MODES.CREDIT_CARD;
    }
    if (paymentProvider === PAYMENT_PROVIDERS.PAYU && !paymentMode) {
      this.paymentMode = PAYMENT_MODES.CREDIT_CARD;
    }
  }

  @action
  addPaymentStrategy(descriptorId: string, paymentStrategy: IPaymentStrategy): void {
    // dynamically add for available payment strategies
    this.paymentStrategies[descriptorId] = paymentStrategy;
  }

  initPayment(paymentProvider: PAYMENT_PROVIDERS, paymentMode?: PAYMENT_MODES): void {
    try {
      // dynamically store available payment strategies
      const paymentDescriptorId = getPaymentDescriptorId(
        paymentProvider,
        paymentMode,
      );
      this.paymentStrategies[paymentDescriptorId]?.setup();
    } finally {
      this.setPayment(paymentProvider, paymentMode);
    }
  }

  pay(payloadData: {}): void {
    const paymentDescriptorId = getPaymentDescriptorId(
      this.paymentProvider,
      this.paymentMode,
    );
    const driver = this.paymentStrategies[paymentDescriptorId];
    driver?.pay(payloadData);
  }

  isNewPayment(paymentProvider: PAYMENT_PROVIDERS, paymentMode?: PAYMENT_MODES): boolean {
    return this.paymentProvider !== paymentProvider || this.paymentMode !== paymentMode;
  }
}
